import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import { addPhone, verifyPhone } from '../../services/authApi';

const AddPhoneModal = ({ open, onClose }) => {
  const { token, linkPhone } = useAuth();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (!phone || digits.length < 10) {
      setError('Enter a valid mobile number (min 10 digits)');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await addPhone(phone, token);
      setStep('otp');
      setCountdown(120);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await linkPhone(phone, otp);
      onClose();
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setError('');
    try {
      await addPhone(phone, token);
      setCountdown(120);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    }
  };

  const formatCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
      backgroundColor: 'rgba(232, 233, 237, 1)',
      borderRadius: '8px',
      padding: '6px 10px',
      '& fieldset': { borderColor: 'transparent' },
      '&:hover fieldset': { borderColor: '#20202A' },
      '&.Mui-focused fieldset': { borderColor: '#20202A', borderWidth: '0.5px' },
      '& .MuiInputBase-input': { padding: '6px 0', fontSize: { xs: '16px', sm: '16px', md: '14px' } },
    },
    '& .MuiInputBase-input::placeholder': { color: '#999', opacity: 1 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ timeout: 0 }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          width: { xs: '90vw', sm: '440px' },
          maxWidth: '440px',
          m: 2,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1, pt: 2, px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute', right: 16, top: 16, padding: '4px',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px', fontWeight: 700, color: '#20202A',
            textAlign: 'center', width: '100%'
          }}
        >
          Add Mobile Number
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px', fontWeight: 400, color: '#666',
            textAlign: 'center', width: '100%'
          }}
        >
          Link your mobile number for easy login
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1.5 }}>
        <Box
          component="form"
          onSubmit={step === 'phone' ? handleSendOtp : handleVerify}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Host Grotesk' }}>{error}</Alert>
          )}

          {step === 'phone' ? (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px', fontWeight: 500, color: '#20202A', mb: 0.5
                }}
              >
                Mobile Number
              </Typography>
              <TextField
                value={phone}
                onChange={(e) => { let v = e.target.value.replace(/[^\d+]/g, ''); if(v.indexOf('+')>0) v=v.replace(/\+/g,''); setPhone(v); setError(''); }}
                placeholder="+91 9876543210"
                type="tel"
                fullWidth
                disabled={loading}
                inputProps={{ maxLength: 15, inputMode: 'tel' }}
                sx={fieldSx}
              />
            </Box>
          ) : (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px', fontWeight: 500, color: '#20202A', mb: 0.5
                }}
              >
                Enter OTP
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '12px', color: '#666', mb: 1
                }}
              >
                Sent to {phone}
              </Typography>
              <TextField
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="Enter 6-digit OTP"
                type="text"
                fullWidth
                disabled={loading}
                inputProps={{ maxLength: 6, inputMode: 'numeric', style: { letterSpacing: '4px', textAlign: 'center' } }}
                sx={fieldSx}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography
                  onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                  sx={{
                    fontFamily: 'Host Grotesk', fontSize: '13px', color: '#666', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Change number
                </Typography>
                <Typography
                  onClick={handleResend}
                  sx={{
                    fontFamily: 'Host Grotesk', fontSize: '13px',
                    color: countdown > 0 ? '#999' : '#1976d2',
                    cursor: countdown > 0 ? 'default' : 'pointer',
                    '&:hover': { textDecoration: countdown > 0 ? 'none' : 'underline' }
                  }}
                >
                  {countdown > 0 ? `Resend in ${formatCountdown(countdown)}` : 'Resend OTP'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, flexDirection: 'column', gap: 1 }}>
        <Button
          onClick={step === 'phone' ? handleSendOtp : handleVerify}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            '&:hover': { backgroundColor: '#333' },
            '&:disabled': { backgroundColor: '#ccc' }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : (step === 'phone' ? 'Send OTP' : 'Verify & Link')}
        </Button>
        <Button
          onClick={onClose}
          variant="text"
          fullWidth
          sx={{
            fontFamily: 'Host Grotesk', fontSize: '14px', color: '#666',
            textTransform: 'none', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          Skip for now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPhoneModal;
